import { useEffect } from 'react';
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  Navigate,
  Outlet,
  useLocation,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useNavigationType,
} from 'react-router-dom';
import { Box, CircularProgress, styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { DownloadReportPage } from '@ldt/features/download_report/download_report_page';
import { queryClient } from 'src/api/query_client';
import { withAuthAction } from 'src/app/action_utils';
import { authMiddleware } from 'src/app/auth_middleware';
import { DependencyContainer } from 'src/app/dependency_container';
import { OrderDetailErrorElement } from 'src/app/error_elements/order_detail_error_element';
import { RootBoundary } from 'src/app/error_elements/root_boundary';
import { Layout } from 'src/app/layout';
import {
  loginLoader,
  callbackLoader,
  rootLayoutLoader,
} from 'src/app/loaders/auth_loaders';
import {
  downloadReportLoader,
  downloadReportAction,
} from 'src/app/loaders/download_report_loaders';
import { createLayoutLoader } from 'src/app/loaders/layout_loaders';
import {
  invitesLoader,
  memberAction,
  membersLoader,
} from 'src/app/loaders/member_loaders';
import {
  orderLoader,
  ordersLoader,
  orderAction,
  orderReportRevisionLoader,
  shouldRevalidateOrderEdit,
} from 'src/app/loaders/order_loaders';
import { createOrganizationMiddleware } from 'src/app/organization_middleware';
import { CallbackPage } from 'src/app/pages/login_callback_page';
import { LoginPage } from 'src/app/pages/login_page';
import { MemberManagementPage } from 'src/app/pages/member_management';
import { OrderDetailPage } from 'src/app/pages/order_detail_page';
import { OrderFormPage } from 'src/app/pages/order_form_page';
import { OrderListPage } from 'src/app/pages/order_list_page';
import { OrderReviseReportPage } from 'src/app/pages/order_revise_report_page';
import { getConfig } from 'src/config/config';
import { getEnvironment, getPlatform } from 'src/config/environment';
import { AuthProvider, ROOT_LAYOUT_ROUTE_ID } from 'src/hooks/use_auth';
import { Environment, DistributionPlatform } from 'src/types/env';

const LoadingIndicatorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100vw',
  backgroundColor: theme.palette.background.paper,
}));

const LoadingIndicator = () => (
  <LoadingIndicatorContainer>
    <CircularProgress />
  </LoadingIndicatorContainer>
);

// Don't initialize Sentry in local environment so people don't get tons of email alerts.
if (
  [
    Environment.Development,
    Environment.Staging,
    Environment.Production,
  ].includes(getEnvironment())
) {
  Sentry.init({
    dsn: 'https://ea252eb64e55443af4d0d3f50062e64c@o1043105.ingest.us.sentry.io/4509567267176448',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.graphqlClientIntegration({
        endpoints: [getConfig().apiBaseUrl + '/graphql'],
      }),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: getConfig().sentryTracesSampleRate,
    environment: getConfig().sentryEnvironment,
    ignoreErrors: ['Failed to fetch'],
    sendDefaultPii: false,
  });
}

// Call sentryCreateBrowserRouter AFTER Sentry.init()
export const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// Helper hook to get dependency container
const useDependencyContainer = (): DependencyContainer => {
  const dependencyContainer = DependencyContainer.getInstance();

  return dependencyContainer;
};

/**
 * Higher Order Component that injects dependencies into a component.
 *
 * @param Component - The component to wrap
 * @param getProps - Function that maps DependencyContainer to component props
 * @returns A new component that handles loading state and dependency injection
 */
function withDeps<P extends object, D extends Partial<P> = Partial<P>>(
  Component: React.ComponentType<P>,
  getProps: (deps: DependencyContainer) => D,
): React.ComponentType<Omit<P, keyof D>> {
  return (props: Omit<P, keyof D>) => {
    const dependencyContainer = useDependencyContainer();

    const dependencyProps = getProps(dependencyContainer);
    return (
      <Component {...({ ...dependencyProps, ...props } as unknown as P)} />
    );
  };
}

const AuthenticatedRootWithDeps = withDeps(
  () => (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  ),
  () => ({}),
);

const LayoutWithDeps = withDeps(Layout, (deps) => ({
  appRepository: deps.appRepository,
  orderRepository: deps.orderRepository,
  uploadWorker: deps.uploadWorker,
}));

const OrderListPageWithDeps = withDeps(OrderListPage, (deps) => ({
  orderRepository: deps.orderRepository,
}));

const OrderFormPageWithDeps = withDeps(OrderFormPage, (deps) => ({
  orderRepository: deps.orderRepository,
  memberRepository: deps.memberRepository,
  orderDetailDisplayer: deps.orderDetailDisplayer,
}));

const OrderDetailPageWithDeps = withDeps(OrderDetailPage, (deps) => ({
  orderRepository: deps.orderRepository,
  orderDetailDisplayer: deps.orderDetailDisplayer,
}));

const OrderReviseReportPageWithDeps = withDeps(
  OrderReviseReportPage,
  (deps) => ({
    orderRepository: deps.orderRepository,
  }),
);

const MemberManagementPageWithDeps = withDeps(MemberManagementPage, (deps) => ({
  memberRepository: deps.memberRepository,
}));

const getDownloadReportRoute = () => {
  if (getPlatform() === DistributionPlatform.Ldt) {
    return [
      {
        id: 'download_report',
        path: '/shared-reports/:token',
        element: <DownloadReportPage />,
        errorElement: <RootBoundary />,
        loader: async (args: LoaderFunctionArgs) =>
          downloadReportLoader(queryClient)(args),
        action: withAuthAction((args: ActionFunctionArgs) =>
          downloadReportAction(queryClient)(args),
        ),
      },
    ];
  }
  return [];
};

const getOrderReviseRoutes = () => {
  if (getPlatform() === DistributionPlatform.Ldt) {
    return [
      {
        id: 'order_revise_report',
        path: 'orders/:id/revise_report',
        element: <OrderReviseReportPageWithDeps />,
        errorElement: <OrderDetailErrorElement />,
        loaderErrorElement: <LoadingIndicator />,
        loader: async (args: LoaderFunctionArgs) => {
          const dependencyContainer = DependencyContainer.getInstance();
          return orderReportRevisionLoader(
            queryClient,
            dependencyContainer.orderRepository,
          )(args);
        },
        action: withAuthAction(async (args: ActionFunctionArgs) => {
          const dependencyContainer = DependencyContainer.getInstance();
          return orderAction(
            queryClient,
            dependencyContainer.orderRepository,
          )(args);
        }),
      },
    ];
  }

  return [];
};

export const browserRouter = sentryCreateBrowserRouter([
  // Public routes first so they match before the root's :organization param
  // (otherwise /login would match as organization='login' and render Layout, not LoginPage)
  {
    id: 'login',
    path: '/login',
    element: <LoginPage />,
    loader: loginLoader,
  },
  {
    id: 'login_callback',
    path: '/callback',
    element: <CallbackPage />,
    loader: callbackLoader,
  },
  ...getDownloadReportRoute(),
  {
    id: ROOT_LAYOUT_ROUTE_ID,
    path: '/',
    middleware: [authMiddleware, createOrganizationMiddleware(queryClient)],
    element: <AuthenticatedRootWithDeps />,
    errorElement: <RootBoundary />,
    hydrateFallbackElement: <LoadingIndicator />,
    loader: rootLayoutLoader,
    children: [
      {
        id: 'organization_layout',
        path: ':organization',
        element: <LayoutWithDeps />,
        loader: async (args: LoaderFunctionArgs) => {
          const dependencyContainer = DependencyContainer.getInstance();
          return createLayoutLoader(
            queryClient,
            dependencyContainer.appRepository,
          )(args);
        },
        children: [
          {
            path: '',
            children: [
              {
                index: true,
                element: <Navigate to='orders' replace />,
              },
              {
                id: 'order_list',
                path: 'orders',
                element: <OrderListPageWithDeps />,
                loader: async (args: LoaderFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return ordersLoader(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                },
              },
              {
                id: 'order_new',
                path: 'orders/new',
                element: <OrderFormPageWithDeps formType='new' />,
                action: withAuthAction(async (args: ActionFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return orderAction(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                }),
              },
              {
                id: 'order_edit',
                path: 'orders/:id/edit',
                element: <OrderFormPageWithDeps formType='edit' />,
                errorElement: <OrderDetailErrorElement />,
                loader: async (args: LoaderFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return orderLoader(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                },
                action: withAuthAction(async (args: ActionFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return orderAction(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                }),
                shouldRevalidate: shouldRevalidateOrderEdit,
              },
              ...getOrderReviseRoutes(),
              {
                id: 'order_detail',
                path: 'orders/:id',
                element: <OrderDetailPageWithDeps />,
                errorElement: <OrderDetailErrorElement />,
                loader: async (args: LoaderFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return orderLoader(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                },
                action: withAuthAction(async (args: ActionFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return orderAction(
                    queryClient,
                    dependencyContainer.orderRepository,
                  )(args);
                }),
              },
              {
                id: 'members',
                path: 'members',
                element: <MemberManagementPageWithDeps initialTabIndex={0} />,
                loader: async (args: LoaderFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return membersLoader(
                    queryClient,
                    dependencyContainer.memberRepository,
                  )(args);
                },
                action: withAuthAction(async (args: ActionFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return memberAction(
                    queryClient,
                    dependencyContainer.memberRepository,
                  )(args);
                }),
              },
              {
                id: 'invites',
                path: 'invites',
                element: <MemberManagementPageWithDeps initialTabIndex={1} />,
                loader: async (args: LoaderFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return invitesLoader(
                    queryClient,
                    dependencyContainer.memberRepository,
                  )(args);
                },
                action: withAuthAction(async (args: ActionFunctionArgs) => {
                  const dependencyContainer = DependencyContainer.getInstance();
                  return memberAction(
                    queryClient,
                    dependencyContainer.memberRepository,
                  )(args);
                }),
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to='/' replace />,
          },
        ],
      },
    ],
  },
]);
